import React, { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";
import { products, featuredIds, productById } from "../data/products";
import { AccessCard } from "./AccessCard";

const FILTER_GROUPS = [
  { key: "all", labelKey: "filterAll", match: () => true },
  { key: "beginner", labelKey: "filterBeginner", match: (p) => p.level.includes("Beginner") },
  { key: "intermediate", labelKey: "filterIntermediate", match: (p) => p.level.includes("Intermediate") },
  { key: "advanced", labelKey: "filterAdvanced", match: (p) => p.level.includes("Advanced") },
  { key: "pro", labelKey: "filterPro", match: (p) => p.level.includes("Pro") },
  { key: "guided", labelKey: "filterGuided", match: (p) => p.category === "guidedLearning" },
  { key: "manual", labelKey: "filterManual", match: (p) => p.category === "manualAnalysis" || p.format.some((f) => f.toLowerCase().includes("manual")) },
  { key: "session", labelKey: "filterSession", match: (p) => p.category === "sessionSystems" },
  { key: "mirror", labelKey: "filterMirror", match: (p) => p.category === "mirrorObservation" },
  { key: "analytics", labelKey: "filterAnalytics", match: (p) => p.category === "advancedAnalytics" },
  { key: "miniApp", labelKey: "filterMiniApp", match: (p) => p.id === "pocket-era" },
  { key: "mt5", labelKey: "filterMt5", match: (p) => p.id === "vertex-mt5-lab" },
  { key: "code", labelKey: "filterCode", match: (p) => p.id === "learning-access-code" },
];

export const FeaturedSection = ({ onOpen, onPlay }) => {
  const { t } = useLanguage();
  return (
    <section className="relative py-24 md:py-32 px-5 md:px-10 lg:px-16" data-testid="featured-section">
      <div className="max-w-[1440px] mx-auto">
        <div className="mb-14 md:mb-18 flex flex-col md:flex-row md:items-end md:justify-between gap-5">
          <div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#A9824A] mb-3">
              · 06 / Featured
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-[#F5F1EA] leading-[0.98]">
              {t("featuredTitle")}
            </h2>
          </div>
          <p className="md:max-w-md text-[15px] text-[#E6E0D6]/80 leading-relaxed">{t("featuredSub")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {featuredIds.map((id, i) => {
            const product = productById(id);
            return product ? <AccessCard key={id} product={product} index={i} onOpen={onOpen} onPlay={onPlay} /> : null;
          })}
        </div>
      </div>
    </section>
  );
};

export const AccessGallery = ({ onOpen, onPlay }) => {
  const { t } = useLanguage();
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const group = FILTER_GROUPS.find((g) => g.key === filter) || FILTER_GROUPS[0];
    let list = products.filter(group.match);
    if (query) {
      const q = query.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.categoryLabel.toLowerCase().includes(q) ||
          p.environment.toLowerCase().includes(q) ||
          p.format.join(" ").toLowerCase().includes(q) ||
          p.badges.join(" ").toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => a.rank - b.rank);
  }, [filter, query]);

  return (
    <section id="accesses" className="relative py-24 md:py-32 px-5 md:px-10 lg:px-16" data-testid="gallery-section">
      <div className="max-w-[1440px] mx-auto">
        <div className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-5">
          <div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#A9824A] mb-3">
              · 18 Access Formats
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-[#F5F1EA] leading-[0.98]">
              {t("galleryTitle")}
            </h2>
            <p className="mt-5 max-w-2xl text-[15px] text-[#E6E0D6]/80 leading-relaxed">{t("gallerySub")}</p>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6 relative max-w-xl">
          <Search size={14} className="absolute left-5 top-1/2 -translate-y-1/2 text-[#A9824A]" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("searchPlaceholder")}
            className="w-full bg-[#15171c] border border-[#D8DCE2]/10 rounded-full py-3.5 pl-12 pr-5 text-[#F5F1EA] text-sm placeholder:text-[#D8DCE2]/40 focus:border-[#D7B56D]/40 focus:outline-none transition-colors duration-200"
            data-testid="gallery-search-input"
          />
        </div>

        {/* Filters */}
        <div className="mb-12 flex flex-wrap gap-2" data-testid="gallery-filters">
          {FILTER_GROUPS.map((g) => (
            <button
              key={g.key}
              onClick={() => setFilter(g.key)}
              className={`px-4 py-2 rounded-full text-[11px] font-mono uppercase tracking-[0.18em] border transition-all duration-200 ${
                filter === g.key
                  ? "bg-[#D7B56D] border-[#D7B56D] text-[#0a0b0f]"
                  : "bg-transparent border-[#D8DCE2]/14 text-[#D8DCE2]/75 hover:border-[#D7B56D]/40 hover:text-[#D7B56D]"
              }`}
              data-testid={`filter-${g.key}`}
            >
              {t(g.labelKey)}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="py-20 text-center"
            data-testid="gallery-no-results"
          >
            <p className="text-[#D8DCE2]/60">{t("noResults")}</p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {filtered.map((product, i) => (
              <AccessCard key={product.id} product={product} index={i} onOpen={onOpen} onPlay={onPlay} />
            ))}
          </div>
        )}

        <p className="mt-14 text-[12px] text-[#D8DCE2]/45 max-w-xl leading-relaxed" data-testid="gallery-disclaimer">
          {t("disclaimer")}
        </p>
      </div>
    </section>
  );
};
